import React from "react";
import novalogo from '../assets/novalogo.png';
import ipmalogo from '../assets/ipmalogo.png';
import sulogo from '../assets/suLogo.png';
import { useTranslation } from "react-i18next";
import { FaLinkedinIn, FaGithub } from 'react-icons/fa';
import { FaUser } from 'react-icons/fa';
import Navbar from "../components/navbar";
import ScrollReveal from '../components/Animation/ScrollReveal';

const About = () => {
  const { t } = useTranslation();

  const researchers = [
    { name: "Ludwig Krippahl", role: "researcher", email: "ludi.krippahl@gmail.com" },
    { name: "Marta Lopes", role: "researcher", email: "mib.lopes@fct.unl.pt" },
    { name: "Pedro Reis Costa", role: "researcher", email: "prcosta@ipma.pt" }
  ];

  const suMembers = [
    { name: "Ramez Elobeid", role: "Software Engineer", 
      linkedin: "https://www.linkedin.com/in/relobeid", github: "https://github.com/relobeid"},
    { name: "Danish Ahsan", role: "Software Engineer", 
      linkedin: "https://www.linkedin.com/in/ahsandanishprogammer/", github: "https://github.com/Danishahsancs"},
    { name: "Hamza Chaudhry", role: "Software Engineer", 
      linkedin: "https://www.linkedin.com/in/hamzachaudhry03", github: "https://github.com/wazoq" },
    { name: "Ali Ranjha", role: "Software Engineer", 
      linkedin: "https://www.linkedin.com/in/ali-ranjha-swd", github: "https://github.com/aran1016" }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 flex flex-col">
      <Navbar />
      <div className="flex-1 py-16 px-6 lg:px-16">
      {/* Hero Section */}
      <section className="max-w-6xl mx-auto text-center mb-20">
        <h1 className="text-5xl font-bold text-gray-900 mb-6 mt-12">
          <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-cyan-500">
            {t("about-us.title")}
          </span>
        </h1>
        <p className="text-lg text-gray-600 max-w-4xl mx-auto mb-12">
          {t("about-us.collaboration_description")}
        </p>
        
        {/* Partner Logos */}
        <div className="flex flex-col md:flex-row justify-center items-center gap-8 md:gap-16">
          <ScrollReveal delay={0} className="group">
            <div className="transition-all duration-300 hover:scale-105">
              <div className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-8 border border-gray-100">
                <img
                  src={novalogo}
                  alt="NOVA School of Science & Technology"
                  className="h-16 md:h-20 object-contain mx-auto filter transition-all duration-300 group-hover:brightness-110"
                />
              </div>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={80} className="group">
            <div className="transition-all duration-300 hover:scale-105">
              <div className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-8 border border-gray-100">
                <img
                  src={ipmalogo}
                  alt="IPMA - Portuguese State Laboratory"
                  className="h-16 md:h-20 object-contain mx-auto filter transition-all duration-300 group-hover:brightness-110"
                />
              </div>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={160} className="group">
            <div className="transition-all duration-300 hover:scale-105">
              <div className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-8 border border-gray-100">
                <img
                  src={sulogo}
                  alt="Salisbury University"
                  className="h-16 md:h-20 object-contain mx-auto filter transition-all duration-300 group-hover:brightness-110"
                />
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Researchers Section */}
      <section className="max-w-6xl mx-auto text-center mb-20">
        <h2 className="text-4xl font-bold mb-12">
          <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-cyan-500">
            {t("about-us.researchers")}
          </span>
          <span className="block mt-2 w-24 h-1 bg-gradient-to-r from-blue-600 to-cyan-500 mx-auto rounded-full"></span>
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {researchers.map((member, index) => (
            <ScrollReveal key={index} delay={index * 60}>
              <div className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300">
                <div className="w-40 h-40 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-full flex items-center justify-center mx-auto mb-6">
                  <FaUser className="text-5xl text-cyan-600" />
                </div>
                <h3 className="text-2xl font-semibold text-gray-800 mb-2">{member.name}</h3>
                <p className="text-gray-500 italic mb-4">{t(`about-us.roles.${member.role}`)}</p>
                <a href={`mailto:${member.email}`} className="inline-block px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                  {t("about-us.contact")}
                </a>
              </div>
            </ScrollReveal>
          ))}
        </div>
      </section>

      {/* Salisbury University Class Section */}
      <section className="max-w-6xl mx-auto text-center mb-20 bg-gradient-to-b from-blue-100 to-cyan-50 rounded-2xl py-16 px-8">
        <h2 className="text-4xl font-bold mb-12">
          <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-cyan-500">
            Salisbury University - COSC 426
          </span>
          <span className="block mt-2 w-24 h-1 bg-gradient-to-r from-blue-600 to-cyan-500 mx-auto rounded-full"></span>
        </h2>

        <p className="text-lg text-gray-700 max-w-3xl mx-auto mb-12">
          This website was developed as part of the COSC 426 Software Engineering course at Salisbury University.
          Under the guidance of our professor, our team collaborated to design and implement a complete data visualization
          platform supporting NOVA and IPMA in their environmental research.
        </p>

        {/* Professor */}
        <div className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300 mb-12 max-w-md mx-auto">
          <h3 className="text-2xl font-semibold text-gray-800 mb-2">Dr. Xiaohong Wang</h3>
          <p className="text-gray-500 italic mb-4">Course Instructor – COSC 426</p>
          <a href="mailto:XSWANG@salisbury.edu" className="inline-block px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
            Contact
          </a>
        </div>

        {/* Developers Section */}
      <section className="max-w-6xl mx-auto text-center mb-8">
        <h2 className="text-4xl font-bold mb-12">
          <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-cyan-500">
            {t("about-us.developers")}
          </span>
          <span className="block mt-2 w-24 h-1 bg-gradient-to-r from-blue-600 to-cyan-500 mx-auto rounded-full"></span>
        </h2>
      </section>

        {/* Students */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {suMembers.map((member, index) => (
            <ScrollReveal key={index} delay={index * 80}>
              <div className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300">
                <div className="w-24 h-24 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FaUser className="text-4xl text-cyan-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">{member.name}</h3>
                <p className="text-gray-500 italic mb-4">{member.role}</p>
                <div className="flex justify-center space-x-3">
                  <a href={member.linkedin} target="_blank" rel="noopener noreferrer" className="text-blue-800 hover:text-blue-600 transition-colors">
                    <FaLinkedinIn className="w-6 h-6" />
                  </a>
                  <a href={member.github} target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-gray-900 transition-colors">
                    <FaGithub className="w-6 h-6" />
                  </a>
                </div>
              </div>
            </ScrollReveal>
          ))}
        </div>
      </section>

      </div>
    </div>
  );
};

export default About;
